<template>
  <div class="irp-contacts-tab" data-testid="irp-contacts-tab">
    <template v-if="!hasError">
      <div class="irp-contacts-tab__header">
        <h3 class="irp-contacts-tab__heading">Your Contacts</h3>
        <IRPExpandToggle
          v-if="ownList?.hasHiddenFields"
          v-model:is-expanded="isOwnExpanded"
          :cta="IRP_CTAS.OWN_DATA"
          data-testid="irp-contacts-tab-expand-toggle"
        />
      </div>
      <div :class="{ 'irp-contacts-tab__card': hasOwnContacts }">
        <div
          v-if="isInitialLoading"
          class="irp-contacts-tab__skeleton"
          data-testid="irp-contacts-tab-skeleton"
        >
          <BittsBone v-for="row in SKELETON_ROW_COUNT" :key="row" type="copy" />
        </div>
        <IRPContactList
          v-else
          ref="ownList"
          :contacts="ownContacts"
          :is-own-data="true"
          empty-message="No contacts on this record"
        />
      </div>
    </template>

    <IRPPartnerDataSection section="contacts">
      <template #default="{ partner }">
        <div class="irp-contacts-tab__card">
          <IRPContactList
            :contacts="partnerContacts(partner)"
            :is-own-data="false"
          />
        </div>
      </template>
    </IRPPartnerDataSection>
  </div>
</template>

<script setup lang="ts">
import { BittsBone } from '@crossbeam/bitts';
import { IRP_CTAS } from '@crossbeam/itly';

import { computed, onBeforeUnmount, useTemplateRef } from 'vue';

import { useIndividualRecordStore } from '@/stores';
import type {
  RecordContactCollectionType,
  RecordPartnerType,
} from '@/types/individual_record';

import IRPContactList from './IRPContactList.vue';
import IRPExpandToggle from './IRPExpandToggle.vue';
import IRPPartnerDataSection from './IRPPartnerDataSection.vue';
import { useProvideHasSalesforceFeed } from './useProvideHasSalesforceFeed';

const SKELETON_ROW_COUNT = 3;

const individualRecordStore = useIndividualRecordStore();

useProvideHasSalesforceFeed();

const isInitialLoading = computed(
  () =>
    !!individualRecordStore.contacts?.isLoading &&
    !individualRecordStore.contacts?.hasLoaded,
);

const hasError = computed(() => !!individualRecordStore.contacts?.error);

const ownContacts = computed(
  () =>
    (
      individualRecordStore.contacts?.own?.contacts as
        | RecordContactCollectionType
        | undefined
    )?.items ?? [],
);

const hasOwnContacts = computed(
  () => isInitialLoading.value || !!ownContacts.value.length,
);

const ownList = useTemplateRef<InstanceType<typeof IRPContactList>>('ownList');

const isOwnExpanded = computed({
  get: () => !!ownList.value?.isEveryRowOpen,
  set: (isExpanded: boolean) => ownList.value?.setAllRowsOpen(isExpanded),
});

function partnerContacts(partner: RecordPartnerType<'contacts'>) {
  return (
    (partner.contacts as RecordContactCollectionType | undefined)?.items ?? []
  );
}

onBeforeUnmount(() => individualRecordStore.focusContact(null));
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";
.irp-contacts-tab {
  @apply flex flex-col gap-16;
}

.irp-contacts-tab__header {
  @apply flex items-center justify-between gap-8;
}

.irp-contacts-tab__heading {
  @apply text-base font-bold text-neutral-text-strong;
}

.irp-contacts-tab__card {
  @apply rounded-16 border border-neutral-border bg-white py-24 pl-24 pr-16 shadow-component;
}

.irp-contacts-tab__skeleton {
  @apply flex flex-col gap-16;
}
</style>
